import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from './DashboardLayout';
import Button from './ui/Button';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, userProfile, loading, signOut } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  // Wait for profile to load before checking role
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // CRITICAL FIX: Only admin and user roles are allowed here
  // Partners authenticate separately via PartnerAuthContext and use PartnerProtectedRoute
  if (userProfile.role !== 'admin' && userProfile.role !== 'user') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <div className="text-3xl">🚫</div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              This area is for administrators and regular users only. Partners should use the Partner Login.
            </p>
            <div className="flex flex-col gap-3 w-full">
              <Button
                variant="default"
                iconName="LogOut"
                iconPosition="left"
                fullWidth
                onClick={async () => {
                  await signOut();
                  window.location.href = '/auth/login';
                }}
              >
                Logout
              </Button>
              <Button
                variant="outline"
                iconName="Users"
                iconPosition="left"
                fullWidth
                onClick={() => window.location.href = '/partner-login'}
              >
                Go to Partner Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wrap content with DashboardLayout for consistent sidebar/header
  return (
    <DashboardLayout>
      {children ? children : <Outlet />}
    </DashboardLayout>
  );
};

export default ProtectedRoute;